import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  getProjects, 
  createProject, 
  updateProject, 
  deleteProject,
  uploadProjectImage,
  deleteProjectImage 
} from '../../services/api';
import '../../styles/Admin.css';

const ManageProjects = () => {
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    location: '',
    year: new Date().getFullYear(),
    status: 'active'
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchProjects();
  }, [navigate]);

 const fetchProjects = async () => {
  try {
    const response = await getProjects();
    const projectsData = Array.isArray(response) 
      ? response 
      : (response.data || []);
    setProjects(projectsData);
  } catch (error) {
    console.error('Error fetching projects:', error);
    setMessage({ type: 'error', text: 'فشل في تحميل المشاريع' });
  } finally {
    setLoading(false);
  }
};
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'year' ? parseInt(value) || '' : value
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'يرجى اختيار ملفات صور فقط' });
        return false;
      }
      if (file.size > 25 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'يجب أن يكون حجم كل صورة أقل من 25 ميجابايت' });
        return false;
      }
      return true;
    });

    setImageFiles(validFiles);
  };

  const openAddModal = () => {
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      location: '',
      year: new Date().getFullYear(),
      status: 'active'
    });
    setImageFiles([]);
    setMessage({ type: '', text: '' });
    setShowModal(true);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '',
      description: project.description || '',
      category: project.category || '',
      location: project.location || '',
      year: project.year || new Date().getFullYear(),
      status: project.status || 'active'
    });
    setImageFiles([]);
    setMessage({ type: '', text: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      location: '',
      year: new Date().getFullYear(),
      status: 'active'
    });
    setImageFiles([]);
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.title) {
    setMessage({ type: 'error', text: 'عنوان المشروع مطلوب' });
    return;
  }

  setSaving(true);
  setMessage({ type: '', text: '' });

  try {
    let projectId;

    if (editingProject) {
      // تحديث مشروع موجود
      await updateProject(editingProject.id, formData);
      projectId = editingProject.id;
      setMessage({ type: 'success', text: 'تم تحديث المشروع بنجاح!' });
    } else {
      // إضافة مشروع جديد
      const response = await createProject(formData);
      console.log('Create response:', response);
      
      // التعامل مع الـ response بشكل صحيح
      if (response.data && response.data.id) {
        projectId = response.data.id;
      } else if (response.id) {
        projectId = response.id;
      } else {
        throw new Error('No project ID returned');
      }
      
      console.log('Project ID:', projectId);
      setMessage({ type: 'success', text: 'تم إنشاء المشروع بنجاح!' });
    }

    // رفع الصور إذا كانت موجودة
    if (imageFiles.length > 0 && projectId) {
      console.log('Uploading images for project:', projectId);
      await uploadImages(projectId);
    }

    // تحديث القائمة
    await fetchProjects();
    
    // إغلاق النافذة بعد ثانيتين
    setTimeout(() => {
      closeModal();
    }, 2000);

  } catch (error) {
    console.error('Error saving project:', error);
    setMessage({ type: 'error', text: 'فشل في حفظ المشروع' });
  } finally {
    setSaving(false);
  }
};

  const uploadImages = async (projectId) => {
  setUploadingImages(true);
  try {
    // إنشاء FormData وإضافة كل الصور
    const formData = new FormData();
    imageFiles.forEach(file => {
      formData.append('images', file);
    });
    
    // رفع كل الصور مرة واحدة
    await uploadProjectImage(projectId, formData);
    
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  } finally {
    setUploadingImages(false);
  }
};

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المشروع؟')) {
      return;
    }

    try {
      await deleteProject(projectId);
      setMessage({ type: 'success', text: 'تم حذف المشروع بنجاح!' });
      await fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      setMessage({ type: 'error', text: 'فشل في حذف المشروع' });
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الصورة؟')) {
      return;
    }

    try {
      await deleteProjectImage(imageId);
      setMessage({ type: 'success', text: 'تم حذف الصورة بنجاح!' });
      await fetchProjects();
    } catch (error) {
      console.error('Error deleting image:', error);
      setMessage({ type: 'error', text: 'فشل في حذف الصورة' });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout" dir="rtl">
      {/* Mobile Menu Button */}
      <button 
        className={`mobile-menu-btn ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2 className="admin-sidebar-logo">لوحة التحكم</h2>
          <p className="admin-sidebar-subtitle">إدارة المعرض</p>
        </div>

        <nav>
          <ul className="admin-nav">
            <li className="admin-nav-item">
              <Link to="/admin/dashboard" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📊</span>
                <span>الرئيسية</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/hero" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🎯</span>
                <span>قسم الرئيسي</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/about" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">👤</span>
                <span>نبذة عني</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/projects" className="admin-nav-link active" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🏗️</span>
                <span>المشاريع</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/contact" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📧</span>
                <span>معلومات التواصل</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/change-password" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🔒</span>
                <span>تغيير كلمة المرور</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <a href="/" className="admin-nav-link" target="_blank" rel="noopener noreferrer">
                <span className="nav-icon">🌐</span>
                <span>عرض الموقع</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span>
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <div>
            <h1>إدارة المشاريع</h1>
            <p>إضافة وتعديل وحذف مشاريعك المعمارية</p>
          </div>
          <button className="btn-add-new" onClick={openAddModal}>
            <span>➕</span>
            إضافة مشروع جديد
          </button>
        </div>

        <div className="admin-content">
          {message.text && !showModal && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          {projects.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">📁</span>
              <p>لا توجد مشاريع بعد</p>
              <button onClick={openAddModal} className="btn-primary">
                أضف مشروعك الأول
              </button>
            </div>
          ) : (
            <div className="projects-grid-admin">
              {projects.map((project) => (
                <div key={project.id} className="project-card-admin">
                  <div className="project-card-image">
                    {project.main_image ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${project.main_image}`}
                        alt={project.title}
                      />
                    ) : (
                      <div className="project-no-image">🏗️</div>
                    )}
                    <span className={`project-status-badge ${project.status}`}>
                      {project.status === 'active' ? 'نشط' : project.status === 'hidden' ? 'مخفي' : project.status}
                    </span>
                  </div>
                  
                  <div className="project-card-content">
                    <h3>{project.title}</h3>
                    {project.category && (
                      <span className="project-category-tag">{project.category}</span>
                    )}
                    <div className="project-meta">
                      {project.location && <span>📍 {project.location}</span>}
                      {project.year && <span>📅 {project.year}</span>}
                    </div>
                    {project.images && project.images.length > 0 && (
                      <div className="project-images-count">
                        📷 {project.images.length} صورة
                      </div>
                    )}
                  </div>

                  <div className="project-card-actions">
                    <button 
                      className="btn-edit"
                      onClick={() => openEditModal(project)}
                    >
                      ✏️ تعديل
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteProject(project.id)}
                    >
                      🗑️ حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-admin">
              <h2>{editingProject ? 'تعديل المشروع' : 'إضافة مشروع جديد'}</h2>
              <button className="modal-close-btn" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body-admin">
              {message.text && (
                <div className={`alert alert-${message.type}`}>
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="admin-form">
                {/* Title */}
                <div className="form-group">
                  <label htmlFor="title">
                    عنوان المشروع *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="مثال: تصميم فيلا حديثة"
                    disabled={saving}
                    required
                  />
                </div>

                {/* Description */}
                <div className="form-group">
                  <label htmlFor="description">
                    الوصف
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="وصف المشروع..."
                    rows="4"
                    disabled={saving}
                  />
                </div>

                {/* Category & Status */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">التصنيف</label>
                    <input
                      type="text"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="مثال: سكني"
                      disabled={saving}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="status">الحالة</label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      disabled={saving}
                    >
                      <option value="active">نشط</option>
                      <option value="hidden">مخفي</option>
                    </select>
                  </div>
                </div>

                {/* Location & Year */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="location">الموقع</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="مثال: بيروت، لبنان"
                      disabled={saving}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="year">السنة</label>
                    <input
                      type="number"
                      id="year"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      min="1900"
                      max="2100"
                      disabled={saving}
                    />
                  </div>
                </div>

                {/* Images */}
                <div className="form-group">
                  <label>
                    صور المشروع
                    <span className="field-hint">رفع صور متعددة (الحد الأقصى 25 ميجابايت لكل صورة)</span>
                  </label>
                  
                  <div className="file-upload-wrapper">
                    <input
                      type="file"
                      id="project-images"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      disabled={saving || uploadingImages}
                    />
                    <label htmlFor="project-images" className="file-upload-label">
                      <span className="upload-icon">📁</span>
                      {imageFiles.length > 0 
                        ? `${imageFiles.length} ملف(ات) محددة` 
                        : 'اختر صور'}
                    </label>
                  </div>
                </div>

                {/* Existing Images (if editing) */}
                {editingProject && editingProject.images && editingProject.images.length > 0 && (
                  <div className="existing-images">
                    <h4>الصور الحالية</h4>
                    <div className="images-grid">
                      {editingProject.images.map((image) => (
                        <div key={image.id} className="image-item">
                          <img
                            src={`${process.env.REACT_APP_API_URL}${image.image_url}`}
                            alt={image.caption || 'المشروع'}
                          />
                          <button
                            type="button"
                            className="btn-delete-image"
                            onClick={() => handleDeleteImage(image.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={saving || uploadingImages}
                  >
                    {saving || uploadingImages ? (
                      <>
                        <span className="spinner-small"></span>
                        {uploadingImages ? 'جاري رفع الصور...' : 'جاري الحفظ...'}
                      </>
                    ) : (
                      <>
                        <span>💾</span>
                        {editingProject ? 'تحديث المشروع' : 'إنشاء المشروع'}
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={closeModal}
                    disabled={saving || uploadingImages}
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProjects;